'use client'

import { ArrowUpRight, List, X } from '@phosphor-icons/react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import CTAButton from './CTAButton'

const navigation = [
  { href: '/services', label: 'Services' },
  { href: '/case-studies', label: 'Case Studies' },
  { href: '/tools', label: 'Tools' },
  { href: '/about', label: 'About' },
  { href: '/blog', label: 'Blog' },
]

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const pathname = usePathname()
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const firstLinkRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => setIsOpen(false), [pathname])

  useEffect(() => {
    if (!isOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    firstLinkRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        menuButtonRef.current?.focus()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen])

  const isActive = (href: string) =>
    pathname === href || (href !== '/' && pathname?.startsWith(`${href}/`))

  return (
    <header className="site-header">
      <div className="portfolio-container site-header-inner">
        <Link href="/" className="site-brand" aria-label="BMateas home">
          <Image
            src="/logo.svg"
            alt="Branislav Mateas"
            width={171}
            height={65}
            loading="eager"
            fetchPriority="high"
            className="site-brand-logo"
          />
        </Link>

        <nav className="site-nav hidden lg:flex" aria-label="Primary navigation">
          {navigation.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              aria-current={isActive(item.href) ? 'page' : undefined}
              className="site-nav-link"
            >
              {item.label}
            </Link>
          ))}
          <CTAButton href="/contact" size="md" variant="filled" className="site-header-contact">
            Contact
            <ArrowUpRight size={17} weight="bold" aria-hidden="true" />
          </CTAButton>
        </nav>

        <button
          ref={menuButtonRef}
          type="button"
          className="site-menu-button"
          onClick={() => setIsOpen((open) => !open)}
          aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={isOpen}
          aria-controls="mobile-navigation"
        >
          {isOpen ? <X size={24} weight="bold" aria-hidden="true" /> : <List size={26} weight="bold" aria-hidden="true" />}
        </button>
      </div>

      {isOpen && (
        <div id="mobile-navigation" className="site-mobile-menu">
          <nav className="mx-auto flex max-w-6xl flex-col px-4 py-6" aria-label="Mobile navigation">
            {navigation.map((item, index) => (
              <Link
                ref={index === 0 ? firstLinkRef : undefined}
                key={item.href}
                href={item.href}
                aria-current={isActive(item.href) ? 'page' : undefined}
                className="site-mobile-link"
              >
                {item.label}
              </Link>
            ))}
            <CTAButton href="/contact" size="lg" variant="filled" className="mt-6 w-full">Contact</CTAButton>
          </nav>
        </div>
      )}
    </header>
  )
}
